/**
 * Suppresses an ALREADY-RUNNING chatbot widget.
 *
 * The page-level `page.chatbot_enabled` switch normally works by never mounting the
 * provider component, so no vendor script is ever injected. That is not enough on
 * client-side navigation: the root layout (and therefore `PageScript`) survives a
 * soft nav, so a visitor moving from a chatbot-on page to a chatbot-off one arrives
 * with the widget already loaded and painted. Unmounting the provider component does
 * not take it away — every vendor bootstraps its UI outside React, into nodes it owns.
 *
 * So the off state is enforced twice: don't mount (first load), and hide what is
 * already there (soft nav). Hiding uses the vendor's own API when it exposes one, and
 * a stylesheet as the fallback that also covers the window between a script being
 * requested and its API appearing on `window`.
 *
 * Tenancy-agnostic: it acts on the widget in the DOM, so it behaves identically in
 * standalone and multi-tenant mode.
 */

/** Toggled stylesheet — one node, added when the chatbot must be hidden, removed when not. */
const STYLE_ELEMENT_ID = "chatbot-page-disabled";

/**
 * Containers the supported vendors render their widgets into. Ids where a vendor uses
 * a stable one, `src` matches otherwise — a bare iframe with no id is still ours if it
 * points at the vendor's domain. Keep in step with the providers `PageScript` gates.
 */
const CHATBOT_CONTAINER_SELECTORS = [
  // Tawk (Tawk.tsx)
  "#tawkchat-container",
  "#tawkchat-minified-wrapper",
  "#tawkchat-minified-container",
  'iframe[src*="tawk.to"]',
  // Kenyt (Kenyt.tsx). The bubble is a plain div, not an iframe — without its id the
  // stylesheet was applied and the bubble stayed on screen.
  "#kenyt-chatbot-container",
  'iframe[src*="kenyt.ai"]',
  "#kenytChatBubble",
  "#kenytChatWindow",
  "#kenytPoweredBy",
  // Freshdesk / Freshchat (Freshdesk.tsx)
  "#fc_frame",
  "#freshworks-container",
  "#freshworks-frame-wrapper",
  // upGrad chatbot (UpgradChatbot.tsx) — iframe ids from the embed's CHATBOT_IFRAME_CONFIGS
  "#chatbot-speeddial-iframe",
  "#chatbot-nudges-iframe",
  "#chatbot-chat-iframe",
];

type TawkApi = { hideWidget?: () => void; showWidget?: () => void };
type FreshchatApi = { hide?: () => void; show?: () => void };
type UpgradChatbotApi = { hide?: () => void; show?: () => void };

function vendorApis(): {
  tawk?: TawkApi;
  freshchat?: FreshchatApi;
  upgrad?: UpgradChatbotApi;
} {
  const w = window as unknown as {
    Tawk_API?: TawkApi;
    fcWidget?: FreshchatApi;
    ChatbotEmbed?: UpgradChatbotApi;
  };
  return { tawk: w.Tawk_API, freshchat: w.fcWidget, upgrad: w.ChatbotEmbed };
}

/**
 * Whether this module has hidden a widget that is still hidden. Restoring is gated on
 * it so `applyChatbotVisibility(true)` on an ordinary page never calls a vendor's
 * "show" on a widget that vendor deliberately keeps hidden (or that was never loaded).
 */
let hiddenByUs = false;

function setStylesheet(hidden: boolean) {
  const existing = document.getElementById(STYLE_ELEMENT_ID);

  if (!hidden) {
    existing?.remove();
    return;
  }
  if (existing) return;

  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  // `!important` because every vendor sets `display` inline on its own container.
  style.textContent = `${CHATBOT_CONTAINER_SELECTORS.join(",")}{display:none !important;}`;
  document.head.appendChild(style);
}

/**
 * Hides (or restores) whatever chatbot is already on the page. Safe to call on every
 * render and before any vendor has loaded: missing APIs and missing nodes are no-ops.
 *
 * Restoring only undoes what this module did — it never starts a widget that was never
 * loaded, which is what makes it safe to call with `enabled: true` on pages that have
 * no chatbot at all.
 */
export function applyChatbotVisibility(enabled: boolean) {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (enabled && !hiddenByUs) return;

  setStylesheet(!enabled);

  const { tawk, freshchat, upgrad } = vendorApis();
  try {
    if (enabled) {
      tawk?.showWidget?.();
      freshchat?.show?.();
      upgrad?.show?.();
    } else {
      tawk?.hideWidget?.();
      freshchat?.hide?.();
      upgrad?.hide?.();
    }
  } catch {
    // A vendor API that throws must not break the page; the stylesheet already
    // enforces the visual state on its own.
  }

  hiddenByUs = !enabled;
}
